import {
  DEFAULT_LOCALE,
  LOCALE_QUERY_PARAM,
  SIMPLIFIED_CHINESE_SCRIPT_SUBTAG,
  SUPPORTED_LOCALES,
  TRADITIONAL_CHINESE_REGION_SUBTAGS,
  TRADITIONAL_CHINESE_SCRIPT_SUBTAG,
} from "constants/i18n";
import { type IDetectedLocale } from "types/i18n/IDetectedLocale";
import { type ILocaleDetectionSignals } from "types/i18n/ILocaleDetectionSignals";
import { LocaleDetectionSource } from "types/i18n/LocaleDetectionSourceEnum";
import { SupportedLocale } from "types/i18n/SupportedLocaleEnum";

/**
 * Picks the locale to start in. A `?lang=` link wins, because opening it is a choice; then the stored
 * choice; then the first browser language a shipped locale serves; then English.
 *
 * @param params.search - `window.location.search`.
 * @param params.storedLocale - The raw stored choice, or null.
 * @param params.navigatorLanguages - `navigator.languages`, most preferred first.
 * @returns The locale and the signal that chose it.
 *
 * Output shape: `{ locale: "zh-TW", source: "BROWSER_LANGUAGE" }`
 */
export function detectPreferredLocale({
  search,
  storedLocale,
  navigatorLanguages,
}: ILocaleDetectionSignals): IDetectedLocale {
  const queryLocale = resolveSupportedLocale({ languageTag: new URLSearchParams(search).get(LOCALE_QUERY_PARAM) });
  if (queryLocale) {
    return { locale: queryLocale, source: LocaleDetectionSource.QueryParam };
  }

  if (storedLocale && isSupportedLocale(storedLocale)) {
    return { locale: storedLocale, source: LocaleDetectionSource.StoredChoice };
  }

  for (const languageTag of navigatorLanguages) {
    const browserLocale = resolveSupportedLocale({ languageTag: languageTag });
    if (browserLocale) {
      return { locale: browserLocale, source: LocaleDetectionSource.BrowserLanguage };
    }
  }

  return { locale: DEFAULT_LOCALE, source: LocaleDetectionSource.Default };
}

/**
 * Maps any BCP 47 language tag to the shipped locale that serves its reader, or null when none does.
 * An explicit script subtag decides first (`zh-Hans-HK` is Simplified); otherwise Taiwan, Hong Kong
 * and Macau read Traditional and every other Chinese tag reads Simplified.
 *
 * @param params.languageTag - A tag such as `"zh-HK"`, `"zh_TW"` or `"en-GB"`; null or empty yields null.
 * @returns The serving locale, or null.
 */
export function resolveSupportedLocale({ languageTag }: { languageTag: string | null }): SupportedLocale | null {
  if (!languageTag) {
    return null;
  }

  const [languageSubtag, ...restSubtags] = languageTag.trim().toLowerCase().replace(/_/g, "-").split("-");
  if (languageSubtag === "en") {
    return SupportedLocale.English;
  }
  if (languageSubtag !== "zh") {
    return null;
  }

  if (restSubtags.includes(SIMPLIFIED_CHINESE_SCRIPT_SUBTAG)) {
    return SupportedLocale.SimplifiedChinese;
  }
  const isTraditional =
    restSubtags.includes(TRADITIONAL_CHINESE_SCRIPT_SUBTAG) ||
    restSubtags.some((subtag) => TRADITIONAL_CHINESE_REGION_SUBTAGS.includes(subtag));
  return isTraditional ? SupportedLocale.TraditionalChinese : SupportedLocale.SimplifiedChinese;
}

/**
 * Narrows a string that is exactly one of the shipped locale tags.
 *
 * @param value - Any string.
 * @returns True when `value` is a `SupportedLocale` tag, compared case-sensitively.
 */
export function isSupportedLocale(value: string): value is SupportedLocale {
  return SUPPORTED_LOCALES.some((locale) => locale === value);
}
